import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from 'react';

const TooltipContext = createContext();

export const useTooltip = () => {
    const context = useContext(TooltipContext);
    if (!context) {
        throw new Error('useTooltip must be used within a TooltipProvider');
    }
    return context;
};

export const TooltipProvider = ({ children }) => {
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipContent, setTooltipContent] = useState('');
    const [tooltipWidth, setTooltipWidth] = useState(0);
    const [tooltipData, setTooltipData] = useState(null);
    const [forceHideTooltip, setForceHideTooltip] = useState(false);
    const [isHighlighting, setIsHighlighting] = useState(false);
    const [showWordLookupLoader, setShowWordLookupLoader] = useState(false);

    const showTooltipAt = useCallback(
        (position, content, width = 0, data = null) => {
            setTooltipPosition(position);
            setTooltipContent(content);
            setTooltipWidth(width);
            setTooltipData(data);
            setShowTooltip(true);
        },
        []
    );

    const hideTooltip = useCallback(() => {
        setShowTooltip(false);
        setTooltipContent('');
        setTooltipData(null);
    }, []);

    const updateTooltipPosition = useCallback(position => {
        setTooltipPosition(position);
    }, []);

    const isTooltipShowing = useCallback(() => showTooltip, [showTooltip]);

    const value = useMemo(
        () => ({
            tooltipPosition,
            showTooltip,
            tooltipContent,
            tooltipWidth,
            tooltipData,
            forceHideTooltip,
            isHighlighting,
            showWordLookupLoader,

            showTooltipAt,
            hideTooltip,
            updateTooltipPosition,
            isTooltipShowing,
            setTooltipPosition,
            setShowTooltip,
            setTooltipContent,
            setTooltipWidth,
            setTooltipData,
            setForceHideTooltip,
            setIsHighlighting,
            setShowWordLookupLoader,
        }),
        [
            tooltipPosition,
            showTooltip,
            tooltipContent,
            tooltipWidth,
            tooltipData,
            forceHideTooltip,
            isHighlighting,
            showWordLookupLoader,
            showTooltipAt,
            hideTooltip,
            updateTooltipPosition,
            isTooltipShowing,
        ]
    );

    return (
        <TooltipContext.Provider value={value}>
            {children}
        </TooltipContext.Provider>
    );
};
